import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { engineApi } from "../../api/engine-api";
import { useAppDispatch } from "../../app/hooks";
import { endGlobalLoadingAction, startGlobalLoadingAction, } from "../../app/reducers/loading-slice";
import { initRoutingRequest } from "../../types/routing-service/initRoutingRequest";
import theme from "../../style/theme";
import {
    OptimizationType,
    RoutingPayload,
    RoutingRequest,
    RoutingType,
} from "../../types/routing-service/routing-request";
import { RoutingResponse } from "../../types/routing-service/routing-response";
import { Task, TaskPayload } from "../../types/routing-service/task";
import { CarType, Vehicle, VehiclePayload, } from "../../types/routing-service/vehicle";
import { addLeadingZero, dateFormat } from "../../utils/date/date-format";
import { ContainedYellowButton } from "../button/ContainedYellowButton";
import { OutlinedGrayButton } from "../button/OutlinedGrayButton";
import { RoutingServiceDateCalendar } from "../calendar/RoutingServiceDateCalendar";
import { RoutingServiceTaskTableHeader } from "../card/RoutingService\bTaskTable\bHead";
import { RoutingServiceTaskTableRow } from "../card/RoutingServiceTaskTableRow";
import { RoutingServiceVehicleTableHeader } from "../card/RoutingServiceVehicleTable\bHead";
import { RoutingServiceVehicleTableRow } from "../card/RoutingServiceVehicleTableRow";
import { RoutingServiceCheckBox } from "../checkbox/RoutingServiceCheckBox";
import InformationIcon from "../icons/InformationIcon";
import { displayCost } from "../../utils/routing-cost/routing-cost-calculator";
import { CostDisplayComponent } from "../label/cost-display-component";
import { runCatchingAsync } from "../../utils/result/result";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { OptimizationOptionRadioButton } from "../radio/OptimizationOptionRadioButton";
import { OptimizationTypeRadioButton } from "../radio/OptimizationTypeRadioButton";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

interface Props {
    setRoutingResult: React.Dispatch<
        React.SetStateAction<RoutingResponse | undefined>
    >;
    payload: RoutingPayload | undefined | null;
}

export const RoutingServiceForm: React.FC<Props> = ({ setRoutingResult, payload }) => {
    const dispatch = useAppDispatch();
    const [routingRequest, setRoutingRequest] =
        useState<RoutingPayload>(initRoutingRequest);

    const [vehicleNumber, setVehicleNumber] = useState(2);
    const [taskNumber, setTaskNumber] = useState(3);

    const [isStaticPickUpAddress, setIsStaticPickUpAddress] = useState(false);
    const [staticPickUpAddress, setStaticPickUpAddress] = useState("");
    const [staticPickUpStartHour, setStaticPickUpStartHour] = useState("");
    const [staticPickUpStartMinute, setStaticPickUpStartMinute] = useState("");
    const [staticPickUpEndHour, setStaticPickUpEndHour] = useState("");
    const [staticPickUpEndMinute, setStaticPickUpEndMinute] = useState("");
    const [staticPickUpServiceTime, setStaticPickUpServiceTime] = useState("");

    useEffect(() => {
        if (routingRequest.routingType === RoutingType.RouteSequential) {
            //TODO
            const firstVehicle = routingRequest.vehicles[0];
            if (firstVehicle.endPlace === undefined) {
                firstVehicle.endPlace = {
                    placeName: "",
                    road: "",
                    latitude: 0,
                    longitude: 0,
                };
            }
            setRoutingRequest({
                ...routingRequest,
                vehicles: [firstVehicle],
            });
        }
    }, [routingRequest.routingType]);

    useDidMountEffect(() => {
        if (payload) {
            setRoutingRequest(payload);
        }
    }, [payload]);

    useEffect(() => {
        if (isStaticPickUpAddress) {
            const firstTask = routingRequest.tasks[0];
            const pickUpAddress = firstTask.pickUpPlace.placeName;
            const pickUpStartHour = firstTask.pickUpTimeWindow.startTimeLimited.hour;
            const pickUpStartMinute =
                firstTask.pickUpTimeWindow.startTimeLimited.minute;
            const pickUpEndHour = firstTask.pickUpTimeWindow.endTimeLimited.hour;
            const pickUpEndMinute = firstTask.pickUpTimeWindow.endTimeLimited.minute;
            const pickUpServiceTime = firstTask.pickUpServiceTime;
            setStaticPickUpAddress(pickUpAddress);
            setStaticPickUpStartHour(pickUpStartHour);
            setStaticPickUpStartMinute(pickUpStartMinute);
            setStaticPickUpEndHour(pickUpEndHour);
            setStaticPickUpEndMinute(pickUpEndMinute);
            setStaticPickUpServiceTime(pickUpServiceTime);

            setRoutingRequest({
                ...routingRequest,
                tasks: routingRequest.tasks.map((task) => ({
                    ...task,
                    pickUpPlace: {
                        ...task.pickUpPlace,
                        placeName: pickUpAddress,
                    },
                    pickUpServiceTime: pickUpServiceTime,
                    pickUpTimeWindow: {
                        startTimeLimited: {
                            hour: pickUpStartHour,
                            minute: pickUpStartMinute,
                        },
                        endTimeLimited: {
                            hour: pickUpEndHour,
                            minute: pickUpEndMinute,
                        },
                    },
                })),
            });
        } else {
            setStaticPickUpAddress("");
        }
    }, [
        isStaticPickUpAddress,
        staticPickUpAddress,
        staticPickUpEndHour,
        staticPickUpEndMinute,
        staticPickUpServiceTime,
        staticPickUpStartHour,
        staticPickUpStartMinute,
    ]);

    const setTargetDate = (date: Date) => {
        setRoutingRequest({ ...routingRequest, targetDate: date });
    };

    const getVehicleNameByIndexOrLength = (indexOrLength: number) => {
        return `배송원${addLeadingZero(
            `${indexOrLength + 1}`
        )}`
    }

    const getTaskNameByIndexOrLength = (indexOrLength: number) => {
        return `물류${addLeadingZero(
            `${indexOrLength + 1}`
        )}`
    }

    const addVehicle = () => {
        setRoutingRequest({
            ...routingRequest,
            vehicles: [
                ...routingRequest.vehicles,
                {
                    vehicleName: getVehicleNameByIndexOrLength(routingRequest.vehicles.length),
                    workTimeWindow: {
                        startTimeLimited: {
                            hour: "",
                            minute: "",
                        },
                        endTimeLimited: {
                            hour: "",
                            minute: "",
                        },
                    },
                    car: {
                        carType: CarType.Ray,
                        maxCapacity: 100,
                    },
                    startPlace: {
                        placeName: "",
                        road: "",
                        latitude: 0,
                        longitude: 0,
                    },
                    endPlace: {
                        placeName: "",
                        road: "",
                        latitude: 0,
                        longitude: 0,
                    },
                },
            ],
        });
        setVehicleNumber(vehicleNumber + 1);
    };

    const deleteVehicle = (index: number) => {
        setRoutingRequest(prevState => {
            const newVehicles = prevState.vehicles
                .filter((_, i) => i !== index)
                .map((vehicle, i) => ({
                    ...vehicle,
                    vehicleName: getVehicleNameByIndexOrLength(i),
                }));
            return { ...prevState, vehicles: newVehicles };
        });
    };

    const editVehicle = (index: number, vehicle: VehiclePayload) => {
        setRoutingRequest(prevState => ({
            ...prevState,
            vehicles: prevState.vehicles.map((v, i) => (i === index ? vehicle : v)),
        }));
    };

    const toggleStaticPickUpAddress = () => {
        if (routingRequest.routingType !== RoutingType.RouteSequential) {
            setIsStaticPickUpAddress(prevState => !prevState);
        }
    };

    const addTask = () => {
        setRoutingRequest({
            ...routingRequest,
            tasks: [
                ...routingRequest.tasks,
                {
                    taskName: getTaskNameByIndexOrLength(routingRequest.tasks.length),
                    pickUpPlace: {
                        placeName: staticPickUpAddress,
                        road: "",
                        latitude: 0,
                        longitude: 0,
                    },
                    pickUpTimeWindow: {
                        startTimeLimited: {
                            hour: staticPickUpStartHour,
                            minute: staticPickUpStartMinute,
                        },
                        endTimeLimited: {
                            hour: staticPickUpEndHour,
                            minute: staticPickUpEndMinute,
                        },
                    },
                    pickUpServiceTime: staticPickUpServiceTime,
                    dropOffPlace: {
                        placeName: "",
                        road: "",
                        latitude: 0,
                        longitude: 0,
                    },
                    dropOffTimeWindow: {
                        startTimeLimited: {
                            hour: "",
                            minute: "",
                        },
                        endTimeLimited: {
                            hour: "",
                            minute: "",
                        },
                    },
                    dropOffServiceTime: "",
                    capacity: 5,
                },
            ],
        });
        setTaskNumber(taskNumber + 1);
    };

    const deleteTask = (index: number) => {
        setRoutingRequest(prevState => {
            const newTasks = prevState.tasks
                .filter((_, i) => i !== index)
                .map((task, i) => ({
                    ...task,
                    taskName: getTaskNameByIndexOrLength(i),
                }));
            return { ...prevState, tasks: newTasks };
        });
    };

    const editTask = (index: number, newTask: TaskPayload) => {
        // 인덱스 0일때 덮어씌워지는 거 방지하기
        if (isStaticPickUpAddress && index === 0) {
            const pickUpAddress = newTask.pickUpPlace.placeName;
            const pickUpStartHour = newTask.pickUpTimeWindow.startTimeLimited.hour;
            const pickUpStartMinute =
                newTask.pickUpTimeWindow.startTimeLimited.minute;
            const pickUpEndHour = newTask.pickUpTimeWindow.endTimeLimited.hour;
            const pickUpEndMinute = newTask.pickUpTimeWindow.endTimeLimited.minute;
            const pickUpServiceTime = newTask.pickUpServiceTime;
            setStaticPickUpAddress(pickUpAddress);
            setStaticPickUpStartHour(pickUpStartHour);
            setStaticPickUpStartMinute(pickUpStartMinute);
            setStaticPickUpEndHour(pickUpEndHour);
            setStaticPickUpEndMinute(pickUpEndMinute);
            setStaticPickUpServiceTime(pickUpServiceTime);
            setRoutingRequest({
                ...routingRequest,
                tasks: routingRequest.tasks.map((task, i) =>
                    i === index
                        ? newTask
                        : {
                            ...task,
                            pickUpPlace: {
                                ...task.pickUpPlace,
                                placeName: pickUpAddress,
                            },
                            pickUpServiceTime: pickUpServiceTime,
                            pickUpTimeWindow: {
                                startTimeLimited: {
                                    hour: pickUpStartHour,
                                    minute: pickUpStartMinute,
                                },
                                endTimeLimited: {
                                    hour: pickUpEndHour,
                                    minute: pickUpEndMinute,
                                },
                            },
                        }
                ),
            });
        } else {
            const newTasks = routingRequest.tasks.map((v, i) =>
                i === index ? newTask : v
            );
            setRoutingRequest({ ...routingRequest, tasks: newTasks });
        }
    };

    const reset = () => {
        setRoutingRequest(initRoutingRequest);
        setVehicleNumber(2);
        setTaskNumber(3);
    };

    const handleSubmit = async (request: RoutingPayload) => {
        dispatch(startGlobalLoadingAction());
        let validate = true;

        const showError = (message: string) => {
            toast.error(message);
            validate = false;
        };

        const isValidTimeFormat = (time: { hour: string, minute: string }) => {
            const hour = Number(time.hour);
            const minute = Number(time.minute);

            if (isNaN(hour) || isNaN(minute)) return false;
            if (hour < 0 || hour > 23) return false;
            if (minute < 0 || minute > 59) return false;

            return true;
        };

        const compareTimeWindows = (start: { hour: string, minute: string }, end: { hour: string, minute: string }) => {
            const startHour = Number(start.hour);
            const startMinute = Number(start.minute);
            const endHour = Number(end.hour);
            const endMinute = Number(end.minute);

            if (startHour < endHour) return true;
            if (startHour > endHour) return false;
            return startMinute < endMinute;
        };

        const validateTimeWindow = (prefix: string, timeWindow: any, index: number) => {
            const { startTimeLimited, endTimeLimited } = timeWindow;

            // 빈 값 체크
            if (startTimeLimited.hour === "" || startTimeLimited.minute === "") {
                showError(`${index + 1}번째 ${prefix}의 시작 시간을 입력해주세요`);
                return false;
            }

            if (endTimeLimited.hour === "" || endTimeLimited.minute === "") {
                showError(`${index + 1}번째 ${prefix}의 종료 시간을 입력해주세요`);
                return false;
            }

            // 유효한 시간 형식 체크
            if (!isValidTimeFormat(startTimeLimited)) {
                showError(`${index + 1}번째 ${prefix}의 시작 시간이 올바르지 않습니다`);
                return false;
            }

            if (!isValidTimeFormat(endTimeLimited)) {
                showError(`${index + 1}번째 ${prefix}의 종료 시간이 올바르지 않습니다`);
                return false;
            }

            // 시작 시간이 종료 시간보다 늦은지 체크
            if (!compareTimeWindows(startTimeLimited, endTimeLimited)) {
                showError(`${index + 1}번째 ${prefix}의 시작 시간이 종료 시간보다 늦습니다`);
                return false;
            }

            return true;
        };

        // 물류의 상하차 시간 순서 검증
        const validateTaskTimeSequence = (task: TaskPayload, index: number) => {
            const pickUpEnd = task.pickUpTimeWindow.endTimeLimited;
            const dropOffStart = task.dropOffTimeWindow.startTimeLimited;

            if (!compareTimeWindows(pickUpEnd, dropOffStart)) {
                showError(`${index + 1}번째 물류의 상차 종료 시간이 하차 시작 시간보다 늦습니다`);
                return false;
            }
            return true;
        };

        // 서비스 시간 검증
        const validateServiceTime = (time: string, prefix: string, type: string, index: number) => {
            const serviceTime = Number(time);
            if (isNaN(serviceTime) || serviceTime <= 0) {
                showError(`${index + 1}번째 ${prefix}의 ${type} 서비스 시간이 올바르지 않습니다`);
                return false;
            }
            return true;
        };

        // 배송원 검증
        for (let [i, vehicle] of request.vehicles.entries()) {
            if (!validateTimeWindow('배송원', vehicle.workTimeWindow, i)) break;

            if (vehicle.startPlace.placeName === "") {
                showError(`${i + 1}번째 배송원의 출발지를 입력해주세요`);
                break;
            }

            if (vehicle.endPlace && vehicle.endPlace.placeName === "") {
                showError(`${i + 1}번째 배송원의 도착지를 입력해주세요`);
                break;
            }

            if (!validate) break;
        }

        if (!validate) {
            dispatch(endGlobalLoadingAction());
            return;
        }

        // 물류 검증
        for (let [i, task] of request.tasks.entries()) {
            if (task.pickUpPlace.placeName === "") {
                showError(`${i + 1}번째 물류의 상차지를 입력해주세요`);
                break;
            }

            if (!validateTimeWindow('물류', task.pickUpTimeWindow, i)) break;
            if (!validateTimeWindow('물류', task.dropOffTimeWindow, i)) break;
            if (!validateTaskTimeSequence(task, i)) break;

            if (!validateServiceTime(task.pickUpServiceTime, '물류', '상차', i)) break;
            if (!validateServiceTime(task.dropOffServiceTime, '물류', '하차', i)) break;

            if (task.dropOffPlace.placeName === "") {
                showError(`${i + 1}번째 물류의 하차지를 입력해주세요`);
                break;
            }

            if (!validate) break;
        }

        if (!validate) {
            dispatch(endGlobalLoadingAction());
            return;
        }

        const formatTime = (date: Date, time: any) =>
            `${dateFormat(date)}T${addLeadingZero(time.hour)}:${addLeadingZero(time.minute)}:00.000+09:00`;

        const vehicles: Vehicle[] = request.vehicles.map(vehicle => ({
            vehicleName: vehicle.vehicleName,
            workTimeWindow: {
                startTimeLimited: formatTime(request.targetDate, vehicle.workTimeWindow.startTimeLimited),
                endTimeLimited: formatTime(request.targetDate, vehicle.workTimeWindow.endTimeLimited),
            },
            car: vehicle.car,
            startPlace: vehicle.startPlace,
            endPlace: vehicle.endPlace,
        }));

        const tasks: Task[] = request.tasks.map(task => ({
            taskName: task.taskName,
            pickUpPlace: task.pickUpPlace,
            pickUpTimeWindow: {
                startTimeLimited: formatTime(request.targetDate, task.pickUpTimeWindow.startTimeLimited),
                endTimeLimited: formatTime(request.targetDate, task.pickUpTimeWindow.endTimeLimited),
            },
            pickUpServiceTime: Number(task.pickUpServiceTime) * 60,
            dropOffPlace: task.dropOffPlace,
            dropOffTimeWindow: {
                startTimeLimited: formatTime(request.targetDate, task.dropOffTimeWindow.startTimeLimited),
                endTimeLimited: formatTime(request.targetDate, task.dropOffTimeWindow.endTimeLimited),
            },
            dropOffServiceTime: Number(task.dropOffServiceTime) * 60,
            capacity: task.capacity,
        }));

        const routingRequest: RoutingRequest = {
            targetDate: dateFormat(request.targetDate),
            routingType: request.routingType,
            optimizationType: request.optimizationType,
            vehicles: vehicles,
            tasks: tasks,
        };

        const result = await runCatchingAsync(async () => {
            return await engineApi.routing(routingRequest);
        });

        result.onSuccess(result => {
            setRoutingResult(result.data);
        });

        dispatch(endGlobalLoadingAction());
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setRoutingRequest(prevState => {
                const oldIndex = prevState.tasks.findIndex(task => task.taskName === active.id);
                const newIndex = prevState.tasks.findIndex(task => task.taskName === over.id);

                const reorderedTasks = arrayMove(prevState.tasks, oldIndex, newIndex);
                // 순서 변경 후 taskName을 인덱스 순서대로 재할당
                const updatedTasks = reorderedTasks.map((task, index) => ({
                    ...task,
                    taskName: getTaskNameByIndexOrLength(index)
                }));

                return {
                    ...prevState,
                    tasks: updatedTasks
                };
            });
        }
    };

    const handleVehicleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setRoutingRequest(prevState => {
                const oldIndex = prevState.vehicles.findIndex(vehicle => vehicle.vehicleName === active.id);
                const newIndex = prevState.vehicles.findIndex(vehicle => vehicle.vehicleName === over.id);

                const reorderedVehicles = arrayMove(prevState.vehicles, oldIndex, newIndex);
                // 순서 변경 후 vehicleName을 인덱스 순서대로 재할당
                const updatedVehicles = reorderedVehicles.map((vehicle, index) => ({
                    ...vehicle,
                    vehicleName: getVehicleNameByIndexOrLength(index)
                }));

                return {
                    ...prevState,
                    vehicles: updatedVehicles
                };
            });
        }
    };

    const handleBulkServiceTimeUpdate = (pickUpTime: string, dropOffTime: string) => {
        setRoutingRequest(prevState => ({
            ...prevState,
            tasks: prevState.tasks.map(task => ({
                ...task,
                pickUpServiceTime: pickUpTime,
                dropOffServiceTime: dropOffTime
            }))
        }));
    };

    return (
        <RoutingServiceFormContainer>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "fit-content",
                    height: "fit-content",
                    margin: 0,
                    boxSizing: "border-box",
                }}
            >
                <DateContainer>
                    <DateContainerHeader>배송 날짜 선택</DateContainerHeader>
                    <DateContainerBody>
                        <RoutingServiceDateCalendar
                            selectedDate={routingRequest.targetDate}
                            setSelectedDate={setTargetDate}
                        />
                        <DateContainerBodySubContent>
                            <InformationIcon fill={theme.palette.primitives.gray600} />
                            <DateContainerBodySubContentLabel>
                                실시간 교통량 적용을 위해 실제 배송을 진행하게 될 날짜를
                                선택해주세요
                            </DateContainerBodySubContentLabel>
                        </DateContainerBodySubContent>
                    </DateContainerBody>
                </DateContainer>
                <RoutingTypeContainer>
                    <RoutingTypeContainerHeader>타입 선택</RoutingTypeContainerHeader>
                    <RoutingTypeContainerBody>
                        <OptimizationTypeRadioButton
                            isActive={
                                routingRequest.routingType === RoutingType.PickupAndDelivery
                            }
                            onClick={() => {
                                setRoutingRequest({
                                    ...routingRequest,
                                    routingType: RoutingType.PickupAndDelivery,
                                    optimizationType: OptimizationType.MinDistance,
                                });
                            }}
                            title={"최적 배차"}
                            content={"N명의 배송원이 M개의 물류를 배송하는 최적 방법"}
                        />
                        <OptimizationTypeRadioButton
                            isActive={
                                routingRequest.routingType === RoutingType.RouteSequential
                            }
                            onClick={() => {
                                if (routingRequest.vehicles.length > 1) {
                                    if (
                                        window.confirm(
                                            `단일 배송원 타입은 배송원을 2명 이상 배치할 수 없습니다. 확인하실 경우 배송원01을 제외한 모든 배송원의 정보가 제거됩니다.`
                                        )
                                    ) {
                                        setRoutingRequest({
                                            ...routingRequest,
                                            vehicles: [routingRequest.vehicles[0]],
                                            routingType: RoutingType.RouteSequential,
                                        });
                                        setIsStaticPickUpAddress(true);
                                    } else {
                                        return;
                                    }
                                } else {
                                    setRoutingRequest({
                                        ...routingRequest,
                                        routingType: RoutingType.RouteSequential,
                                    });
                                    setIsStaticPickUpAddress(true);
                                }
                            }}
                            title={"단일 배송원 시간 측정"}
                            content={"1명의 배송원이 정해진 순서가 있는 물류들을 배송할 때 걸리는 시간 측정"}
                            disabled={true}
                            disabledReason={"장기 미사용으로 인한 서비스 중단"}
                        />
                    </RoutingTypeContainerBody>
                </RoutingTypeContainer>
                {routingRequest.routingType === RoutingType.PickupAndDelivery && (
                    <OptimizationTypeContainer1>
                        {/*<OptimizationTypeWarningContent>*/}
                        {/*  <InformationIcon fill={"#F14800"} />*/}
                        {/*  <OptimizationTypeWarningContentLabel>*/}
                        {/*    최적 배차는 건당 평균 1,000원의 라우팅 비용이 발생합니다.*/}
                        {/*  </OptimizationTypeWarningContentLabel>*/}
                        {/*</OptimizationTypeWarningContent>*/}
                        <OptimizationTypeContainer2>
                            <OptimizationTypeContainerHeader>
                                최적화 옵션
                            </OptimizationTypeContainerHeader>
                            <OptimizationTypeContainerBody>
                                <OptimizationOptionRadioButton
                                    isActive={
                                        routingRequest.optimizationType ===
                                        OptimizationType.MinDistance
                                    }
                                    onClick={() => {
                                        setRoutingRequest({
                                            ...routingRequest,
                                            optimizationType: OptimizationType.MinDistance,
                                        });
                                    }}
                                    optionName={"거리 최적화"}
                                />
                                <OptimizationOptionRadioButton
                                    isActive={
                                        routingRequest.optimizationType ===
                                        OptimizationType.TaskEqual
                                    }
                                    onClick={() => {
                                        setRoutingRequest({
                                            ...routingRequest,
                                            optimizationType: OptimizationType.TaskEqual,
                                        });
                                    }}
                                    optionName={"공동 분배 최적화"}
                                />
                                <OptimizationOptionRadioButton
                                    isActive={
                                        routingRequest.optimizationType ===
                                        OptimizationType.TimeEqual
                                    }
                                    onClick={() => {
                                        setRoutingRequest({
                                            ...routingRequest,
                                            optimizationType: OptimizationType.TimeEqual,
                                        });
                                    }}
                                    optionName={"시간 최적화"}
                                />
                            </OptimizationTypeContainerBody>
                        </OptimizationTypeContainer2>
                    </OptimizationTypeContainer1>
                )}
                <VehicleContainer1>
                    <VehicleContainerHeader>배송원 정보 입력</VehicleContainerHeader>
                    <VehicleContainerBody>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleVehicleDragEnd}
                        >
                            <table style={{
                                borderCollapse: "separate",
                                borderSpacing: "0 14px",
                                width: "fit-content",
                            }}>
                                <thead>
                                    <RoutingServiceVehicleTableHeader />
                                </thead>
                                <SortableContext
                                    items={routingRequest.vehicles.map(vehicle => vehicle.vehicleName)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <tbody>
                                        {routingRequest.vehicles.map((vehicle, i) => (
                                            <RoutingServiceVehicleTableRow
                                                key={vehicle.vehicleName}
                                                index={i}
                                                vehicle={vehicle}
                                                routingType={routingRequest.routingType}
                                                showAdd={routingRequest.routingType === RoutingType.PickupAndDelivery && i === routingRequest.vehicles.length - 1}
                                                showDelete={routingRequest.routingType === RoutingType.PickupAndDelivery && routingRequest.vehicles.length !== 1}
                                                clickAdd={addVehicle}
                                                clickDelete={deleteVehicle}
                                                editVehicle={editVehicle}
                                            />
                                        ))}
                                    </tbody>
                                </SortableContext>
                            </table>
                        </DndContext>
                    </VehicleContainerBody>
                </VehicleContainer1>
                <TaskContainer1>
                    <TaskContainerHeader>물류 정보 입력</TaskContainerHeader>
                    <RoutingServiceCheckBox
                        checked={isStaticPickUpAddress}
                        text1={"상차지 주소 고정하기"}
                        text2={"- *첫 물류의 상차지 정보를 모든 물류의 상차 정보로 고정"}
                        disabled={
                            routingRequest.routingType === RoutingType.RouteSequential
                        }
                        onClick={toggleStaticPickUpAddress}
                    />
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <table
                            style={{
                                borderCollapse: "separate",
                                borderSpacing: "0 14px",
                                tableLayout: "auto",
                                width: "fit-content",
                                height: "fit-content",
                            }}
                        >
                            <thead>
                                <RoutingServiceTaskTableHeader
                                    onBulkServiceTimeUpdate={handleBulkServiceTimeUpdate}
                                />
                            </thead>
                            <SortableContext
                                items={routingRequest.tasks.map(task => task.taskName)}
                                strategy={verticalListSortingStrategy}
                            >
                                <tbody>
                                    {routingRequest.tasks.map((task, i) => (
                                        <RoutingServiceTaskTableRow
                                            key={task.taskName}
                                            index={i}
                                            task={task}
                                            editTask={editTask}
                                            pickUpAddressDiabled={isStaticPickUpAddress && i !== 0}
                                            showAdd={i === routingRequest.tasks.length - 1}
                                            showDelete={routingRequest.tasks.length > 2}
                                            clickAdd={addTask}
                                            clickDelete={deleteTask}
                                        />
                                    ))}
                                </tbody>
                            </SortableContext>
                        </table>
                    </DndContext>
                </TaskContainer1>
                <ButtonContainer>
                    <CostDisplayComponent
                        fontSize={16}
                        backgroundColor={"#FFF7D9"}
                        textColor={"#D6A11B"}
                    >
                        {displayCost(routingRequest.vehicles.length, routingRequest.tasks.length)}
                    </CostDisplayComponent>
                    <OutlinedGrayButton
                        text="초기화"
                        height="51px"
                        width="125px"
                        onClick={reset}
                    />
                    <ContainedYellowButton
                        text="결과보기"
                        height="51px"
                        width="296px"
                        onClick={() => handleSubmit(routingRequest)}
                    />
                </ButtonContainer>
            </div>
        </RoutingServiceFormContainer>
    );
};

const RoutingServiceFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 76%;
  height: 100%;
  flex-grow: 1;
  padding: 24px 0 0 0;
  background: #ffffff;
  box-sizing: border-box;
  overflow: scroll;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DateContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: 67px;
  padding: 0 0 0 64px;
  display: flex;
  flex-direction: row;
  background: #ffffff;
  margin: 0 0 20px 0;
`;

const DateContainerHeader = styled.div`
  color: ${theme.palette.primitives.gray800};
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
  margin: 0 49px 0 0;
`;

const DateContainerBody = styled.div`
  width: 395px;
  height: 100%;
  color: ${theme.palette.primitives.gray800};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DateContainerBodySubContent = styled.div`
  width: 500px;
  height: 17px;
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const DateContainerBodySubContentLabel = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  font-weight: 400;
  vertical-align: middle;
  font-size: 14px;
  color: ${theme.palette.primitives.gray600};
`;

const RoutingTypeContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: 69px;
  padding: 0 0 0 64px;
  display: flex;
  flex-direction: row;
  background: #ffffff;
  margin: 0 0 40px 0;
`;

const RoutingTypeContainerHeader = styled.div`
  color: ${theme.palette.primitives.gray800};
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
  margin: 0 84px 0 0;
`;

const RoutingTypeContainerBody = styled.div`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const OptimizationTypeContainer1 = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: fit-content;
  padding: 0 0 0 64px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  margin: 0 0 40px 0;
  gap: 12px;
`;

const OptimizationTypeWarningContent = styled.div`
  width: 500px;
  height: 17px;
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const OptimizationTypeWarningContentLabel = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  font-weight: 400;
  vertical-align: middle;
  font-size: 14px;
  color: #f14800;
`;

const OptimizationTypeContainer2 = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  display: flex;
  flex-direction: row;
  background: #ffffff;
`;

const OptimizationTypeContainerHeader = styled.div`
  color: ${theme.palette.primitives.gray800};
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
  margin: 0 69px 0 0;
`;

const OptimizationTypeContainerBody = styled.div`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  gap: 20px;
`;

const VehicleContainer1 = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: fit-content;
  padding: 0 0 0 64px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  margin: 0 0 40px 0;
  gap: 12px;
`;

const VehicleContainerHeader = styled.div`
  color: ${theme.palette.primitives.gray800};
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
  margin: 0 84px 0 0;
`;

const VehicleContainerBody = styled.div`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VehicleContainerContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const VehicleContainerButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const TaskContainer1 = styled.div`
  width: fit-content;
  box-sizing: border-box;
  height: fit-content;
  padding: 0 0 0 64px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  margin: 0 0 40px 0;
  gap: 24px;
`;

const TaskContainerHeader = styled.div`
  color: ${theme.palette.primitives.gray800};
  width: fit-content;
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
  margin: 0 84px 0 0;
`;

const TaskContainer2 = styled.div`
  margin: 0;
  width: fit-content;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TaskContainerContent = styled.div`
  display: flex;
  width: fit-content;
  flex-direction: row;
  gap: 12px;
`;

const TaskContainerButtonContent = styled.div`
  display: flex;
  width: fit-content;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin: 0 0 80px 0;
`;
